/**
 * Admin → Audit tab. Read-only ledger of every platform state change with
 * "Sales Only" filter that hydrates each row with buyer/seller usernames,
 * listing title, and a fee-broken-down total. Pure UI — Admin.jsx owns
 * the auditLog + auditSalesOnly state and the load fn.
 */
export default function AdminAuditTab({ auditLog, auditSalesOnly, setAuditSalesOnly }) {
  return (
    <div className="mt-6 space-y-3" data-testid="admin-audit-log">
      <div className="h57-surface p-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Audit Log</div>
          <div className="mt-1 text-xs text-zinc-500">
            Every state change on the platform. Toggle <span className="text-[#FFD700]">Sales Only</span> to
            see just the transactions — buyer, seller, card, and the total amount the buyer paid
            (price + shipping + additional fees).
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAuditSalesOnly(false)}
            data-testid="audit-filter-all"
            className={`h57-chip !py-1.5 !px-3 ${!auditSalesOnly ? "!border-[#FFD700] !text-[#FFD700]" : ""}`}
          >
            All Events
          </button>
          <button
            type="button"
            onClick={() => setAuditSalesOnly(true)}
            data-testid="audit-filter-sales"
            className={`h57-chip !py-1.5 !px-3 ${auditSalesOnly ? "!border-[#FFD700] !text-[#FFD700]" : ""}`}
          >
            Sales Only
          </button>
        </div>
      </div>

      <div className="h57-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Actor</th>
              <th className="text-left p-3">Event</th>
              <th className="text-left p-3">Details</th>
              <th className="text-right p-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {auditLog.map((e) => {
              const isSale = !!e.sale;
              return (
                <tr key={e.id} className="border-b border-white/5 align-top">
                  <td className="p-3 text-zinc-400 text-xs whitespace-nowrap">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="p-3 font-mono text-xs whitespace-nowrap">
                    {e.actor_username
                      ? <span className="text-white">@{e.actor_username}</span>
                      : (e.actor_id ? <span className="text-zinc-500">{e.actor_id.slice(0, 8)}</span> : <span className="text-zinc-600">system</span>)}
                  </td>
                  <td className="p-3 font-mono text-xs text-[#FF5F1F] whitespace-nowrap">{e.event}</td>
                  <td className="p-3 text-xs">
                    {isSale ? (
                      <div className="space-y-1">
                        <div className="font-mono text-zinc-200 whitespace-nowrap">
                          <span className="text-[#007AFF]">@{e.sale.buyer_username || "—"}</span>
                          <span className="text-zinc-500"> → </span>
                          <span className="text-[#FF5F1F]">@{e.sale.seller_username || "—"}</span>
                        </div>
                        <div className="text-zinc-300 max-w-md truncate" title={e.sale.listing_title || ""}>
                          {e.sale.listing_title || <span className="text-zinc-600 italic">(listing removed)</span>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <span className="h57-chip !py-0.5 !px-1.5 !text-[9px]">{e.sale.status}</span>
                          {e.sale.qualifying && (
                            <span className="h57-chip h57-chip-gold !py-0.5 !px-1.5 !text-[9px]">QUALIFYING</span>
                          )}
                          {e.sale.payment_method && (
                            <span className="h57-chip !py-0.5 !px-1.5 !text-[9px] opacity-70">{e.sale.payment_method}</span>
                          )}
                        </div>
                      </div>
                    ) : e.target_type === "user" ? (
                      <span className="font-mono text-zinc-300">
                        @{e.target_username || (e.target_id || "").slice(0, 8)}
                      </span>
                    ) : e.target_type === "listing" ? (
                      <span className="text-zinc-300 truncate block max-w-md" title={e.target_title || ""}>
                        {e.target_title || <span className="text-zinc-500 font-mono">listing:{(e.target_id || "").slice(0, 8)}</span>}
                      </span>
                    ) : e.offer ? (
                      <div className="font-mono text-xs">
                        <span className="text-[#007AFF]">@{e.offer.buyer_username || "—"}</span>
                        <span className="text-zinc-500"> → </span>
                        <span className="text-[#FF5F1F]">@{e.offer.seller_username || "—"}</span>
                        {typeof e.offer.amount === "number" && (
                          <span className="ml-2 text-[#FFD700]">${e.offer.amount.toFixed(2)}</span>
                        )}
                        {e.offer.listing_title && (
                          <div className="text-zinc-400 max-w-md truncate">{e.offer.listing_title}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-zinc-500 font-mono text-[11px]">
                        {e.target_type ? `${e.target_type}:${(e.target_id || "").slice(0, 8)}` : "—"}
                      </span>
                    )}
                    {e.meta && Object.keys(e.meta).length > 0 && !isSale && (
                      <div className="mt-1 text-zinc-500 text-[10px] font-mono max-w-md truncate" title={JSON.stringify(e.meta)}>
                        {JSON.stringify(e.meta)}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right align-top">
                    {isSale ? (
                      <div className="inline-block text-right">
                        <div
                          className="font-display font-black text-emerald-400 tabular-nums text-base whitespace-nowrap"
                          title={`Card: $${e.sale.sale_price.toFixed(2)}\nShipping: $${e.sale.shipping_cost.toFixed(2)}\nAdditional fees: $${e.sale.additional_fees.toFixed(2)}${e.sale.additional_fees_note ? ` (${e.sale.additional_fees_note})` : ""}\nTotal: $${e.sale.total_amount.toFixed(2)}`}
                        >
                          ${e.sale.total_amount.toFixed(2)}
                        </div>
                        <div className="font-mono text-[9px] text-zinc-500 leading-tight mt-0.5">
                          <span title="Card price">${e.sale.sale_price.toFixed(2)}</span>
                          {e.sale.shipping_cost > 0 && <> + <span title="Shipping">${e.sale.shipping_cost.toFixed(2)}&nbsp;ship</span></>}
                          {e.sale.additional_fees > 0 && <> + <span title={e.sale.additional_fees_note || "Additional fees"}>${e.sale.additional_fees.toFixed(2)}&nbsp;fees</span></>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-zinc-700">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {auditLog.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">No events yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
